import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, FileType2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { exportTable, ExportFormat, TableExportData } from '../lib/tableExport';

interface Props {
  /** Construit la matrice au moment du clic : elle suit l'état affiché. */
  getData: () => TableExportData;
  disabled?: boolean;
  className?: string;
}

/**
 * Bouton « Exporter » + choix du format.
 *
 * Chaque format porte la phrase qui dit à quoi il sert : « ouvre directement en
 * tableau », « pour un autre logiciel », « pour imprimer ». C'est le point de
 * la fonctionnalité — un organisateur qui télécharge un CSV et se retrouve avec
 * une colonne unique dans Excel n'a pas fait d'erreur, on lui a juste proposé
 * le mauvais format sans le lui dire.
 */
export const ExportMenu: React.FC<Props> = ({ getData, disabled, className }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const run = async (format: ExportFormat) => {
    setBusy(format);
    try {
      await exportTable(format, getData());
    } catch (err) {
      // Le PDF charge `jspdf-autotable` à la demande : un réseau coupé au
      // mauvais moment fait échouer l'import, il faut le dire.
      console.error('[export]', err);
      toast.error(t('export.failed'));
    } finally {
      setBusy(null);
    }
  };

  const items: Array<{ format: ExportFormat; icon: React.ReactNode; label: string; hint: string }> = [
    {
      format: 'xlsx',
      icon: <FileSpreadsheet size={16} />,
      label: t('export.excel'),
      hint: t('export.excelHint'),
    },
    {
      format: 'csv',
      icon: <FileType2 size={16} />,
      label: t('export.csv'),
      hint: t('export.csvHint'),
    },
    {
      format: 'pdf',
      icon: <FileText size={16} />,
      label: t('export.pdf'),
      hint: t('export.pdfHint'),
    },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled || busy !== null} className={className}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          <span className="ml-2 whitespace-nowrap">{t('export.action')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {items.map(item => (
          <DropdownMenuItem
            key={item.format}
            onClick={() => run(item.format)}
            className="items-start gap-3 py-2"
          >
            <span className="mt-0.5 text-muted-foreground">{item.icon}</span>
            <span className="flex flex-col">
              <span className="font-medium">{item.label}</span>
              <span className="text-xs text-muted-foreground">{item.hint}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportMenu;
